import { useMemo } from 'react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import type { AvailabilityMode } from '../../components/availability/availability-form/types';
import { type CalendarEntityType, useCalendarStore } from '../../stores/calendar.store';
import { transformScheduleDefinitionGroupsToSchedules } from './calendar-schedule-definitions';

interface LocationEntityIds {
  locationId: string;
  providerIds?: string[];
  operatoryIds?: string[];
}

const getAvailabilityModeForCalendarEntityType = (calendarEntityType: CalendarEntityType): AvailabilityMode => {
  return calendarEntityType === 'operatory' ? 'OPERATORY' : 'PROVIDER';
};

// TODO: rename
export const useGetCalendarScheduleV3Data = (locationWiseEntityIds: LocationEntityIds[]) => {
  const { calendarDate, calendarEntityType } = useCalendarStore(['calendarDate', 'calendarEntityType']);
  const locationIds = useMemo(() => locationWiseEntityIds.map((item) => item.locationId), [locationWiseEntityIds]);
  const selectedAvailabilityMode = getAvailabilityModeForCalendarEntityType(calendarEntityType);

  const listLocationSettingsQuery = SchedulerV3.Queries.useListLocationSettingsQuery({
    request: { locationIds },
    options: { enabled: locationIds.length > 0 },
  });

  const availabilityModeByLocationId = useMemo(
    () =>
      locationIds.reduce<Record<string, AvailabilityMode>>((accumulator, locationId) => {
        accumulator[locationId] = (listLocationSettingsQuery.data?.locationsSettings?.[locationId]?.availabilityLens ||
          'PROVIDER') as AvailabilityMode;
        return accumulator;
      }, {}),
    [listLocationSettingsQuery.data?.locationsSettings, locationIds]
  );

  const shouldShowSchedulesByLocationId = useMemo(
    () =>
      locationIds.reduce<Record<string, boolean>>((accumulator, locationId) => {
        accumulator[locationId] =
          !listLocationSettingsQuery.isLoading && availabilityModeByLocationId[locationId] === selectedAvailabilityMode;
        return accumulator;
      }, {}),
    [availabilityModeByLocationId, listLocationSettingsQuery.isLoading, locationIds, selectedAvailabilityMode]
  );

  const scheduleDefinitionQueries = SchedulerV3.Queries.useListScheduleDefinitionsQueries(
    locationIds.map((locationId) => ({
      request: { locationId, availabilityLens: availabilityModeByLocationId[locationId] || 'PROVIDER' },
      options: {
        enabled: !!locationId && shouldShowSchedulesByLocationId[locationId],
      },
    }))
  );

  const scheduleExceptionQueries = SchedulerV3.Queries.useListScheduleExceptionsQueries(
    locationWiseEntityIds.map(({ locationId }) => {
      return {
        request: {
          locationId,
        },
        options: {
          enabled: !!locationId && shouldShowSchedulesByLocationId[locationId],
        },
      };
    })
  );

  const providerSchedulesQuery = useMemo(
    () => ({
      data: scheduleDefinitionQueries
        .map((query, index) => {
          const locationId = locationWiseEntityIds[index]?.locationId ?? '';
          if (!shouldShowSchedulesByLocationId[locationId]) {
            return [];
          }

          const availabilityMode = availabilityModeByLocationId[locationId] || 'PROVIDER';
          const groups =
            availabilityMode === 'OPERATORY'
              ? (query.data?.operatoryGroups || []).map((group) => ({
                  entityId: group.operatoryId,
                  locationId,
                  scheduleDefinitions: group.scheduleDefinitions,
                }))
              : (query.data?.providerGroups || []).map((group) => ({
                  entityId: group.providerId,
                  locationId,
                  scheduleDefinitions: group.scheduleDefinitions,
                }));

          return transformScheduleDefinitionGroupsToSchedules(groups, calendarDate, {
            scheduleExceptions: scheduleExceptionQueries[index]?.data?.exceptions || [],
            entityType: availabilityMode,
          });
        })
        .flat(),
      isLoading:
        listLocationSettingsQuery.isLoading ||
        scheduleDefinitionQueries.some((query) => query.isLoading) ||
        scheduleExceptionQueries.some((query) => query.isLoading),
      isError:
        listLocationSettingsQuery.isError ||
        scheduleDefinitionQueries.some((query) => query.isError) ||
        scheduleExceptionQueries.some((query) => query.isError),
    }),
    [
      availabilityModeByLocationId,
      calendarDate,
      listLocationSettingsQuery.isError,
      listLocationSettingsQuery.isLoading,
      locationWiseEntityIds,
      scheduleDefinitionQueries,
      scheduleExceptionQueries,
      shouldShowSchedulesByLocationId,
    ]
  );

  return {
    providerSchedulesQuery,
  };
};
